import React from 'react';
import { Modal, View } from 'react-native';
import { useT } from '@transifex/react';
import { cn as dsCn } from '@vidahealth/vida-ds';

import { BloomButton } from '../components/BloomButton';
import ThemedText from '../components/ThemedText';
import { useDsEnabled } from '../contexts/DesignSystem';
import { cn } from '../utils/cn';
import { screenTestId } from '../utils/testId';

import { featureLabelFor } from './featureLabel';
import type { BiometricType } from './useBiometricCapabilities';

type Props = {
  visible: boolean;
  biometricType: BiometricType;
  onEnable: () => void;
  onDecline: () => void;
};

const tid = screenTestId('BiometricEnroll');

export const BiometricEnrollmentModal: React.FC<Props> = (props) => {
  const { visible, biometricType, onEnable, onDecline } = props;
  const t = useT();
  const featureLabel = featureLabelFor(t, biometricType);
  const dsEnabled = useDsEnabled();
  const merge = dsEnabled ? dsCn : cn;

  return (
    <Modal
      visible={visible}
      transparent
      animationType='fade'
      onRequestClose={onDecline}
      testID={tid('modal')}
    >
      <View className={merge('flex-1 items-center justify-center p-4', dsEnabled ? 'bg-scrim-overlay' : 'bg-black/50')}>
        <View
          className={merge(
            'w-full max-w-md',
            dsEnabled ? 'rounded-ds-xl bg-canvas p-ds-24' : 'rounded-3xl bg-white p-6 shadow-2xl'
          )}
          testID={tid('card')}
        >
          <ThemedText type='titleDefault' className='mb-3'>
            {t('Enable {feature}?', { feature: featureLabel })}
          </ThemedText>

          <ThemedText type='bodyRegular' color='subtle' className='mb-6 text-login-gray-70'>
            {t(
              'Sign in to Vida faster next time using {feature} instead of your password. You can change this any time in Settings.',
              { feature: featureLabel }
            )}
          </ThemedText>

          <View className='gap-3'>
            <BloomButton
              onPress={onEnable}
              testID={tid('enable-button')}
              text={t('Enable {feature}', { feature: featureLabel })}
            />
            <BloomButton
              hierarchy='secondary'
              onPress={onDecline}
              testID={tid('decline-button')}
              text={t('Not now')}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};
